import GeneralService from "./GeneralService";
import { ApiKeyOrGetter } from "./GeneralService";
import { Component } from "react";

export interface WorkflowRun {
  id: number;
  date_created?: string;
  date_updated?: string;
  workflow_id: number;
  company_id: number;
  application_id?: number;
  session_id: string;
  status: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  error_message?: string;
  profile?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  workflow_name?: string;
}

export interface Workflow {
  id: number;
  workflow_name: string;
  workflow_ref_name: string;
  application_id?: number;
  description?: string;
}

export interface WorkflowTestSetItem {
  id: number;
  date_created?: string;
  test_set_id: number;
  company_id: number;
  workflow_run_id: number;
  meta?: Record<string, unknown>;
  workflow_run?: WorkflowRun;
}

export interface WorkflowTestSet {
  id: number;
  date_created?: string;
  date_updated?: string;
  company_id: number;
  application_id: number;
  test_set_name: string;
  test_set_description?: string;
  meta?: Record<string, unknown>;
  item_count: number;
  application_name?: string;
  items: WorkflowTestSetItem[];
}

export interface WorkflowTestRun {
  id: number;
  date_created?: string;
  date_completed?: string;
  test_set_id: number;
  workflow_id: number;
  company_id: number;
  description?: string;
  status: string;
  config?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  workflow_name?: string;
  item_count: number;
  completed_count: number;
  passed_count: number;
  failed_count: number;
  error_count: number;
  duration_ms?: { avg?: number; p50?: number; p90?: number; p95?: number };
  avg_field_match_rate?: number;
}

export interface DiffField {
  path: string;
  status: "match" | "mismatch" | "missing" | "extra";
  expected: unknown;
  actual: unknown;
}

export interface DiffResult {
  fields: DiffField[];
  matched_count: number;
  total_count: number;
  match_rate: number;
}

export interface WorkflowTestRunItem {
  id: number;
  date_created?: string;
  date_executed?: string;
  test_run_id: number;
  test_set_item_id: number;
  company_id: number;
  status: string;
  expected_output?: Record<string, unknown>;
  actual_output?: Record<string, unknown>;
  matched?: boolean;
  error_message?: string;
  duration_ms?: number;
  diff_result?: DiffResult;
  input?: Record<string, unknown>;
}

export class WorkflowService extends Component {
  public static getWorkflows(
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/workflow/",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createWorkflowTestSet(
    applicationId: number,
    testSetName: string,
    workflowRunIds: number[] | null,
    sessionIds: string[] | null,
    workflowId: number | null,
    testSetDescription: string | undefined,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = {
      application_id: applicationId,
      test_set_name: testSetName,
    };
    if (workflowId) {
      body.workflow_id = workflowId;
    }
    if (testSetDescription) {
      body.test_set_description = testSetDescription;
    }
    if (workflowRunIds && workflowRunIds.length > 0) {
      body.workflow_run_ids = workflowRunIds;
    }
    if (sessionIds && sessionIds.length > 0) {
      body.session_ids = sessionIds;
    }
    return GeneralService.post(
      "/workflow/test-sets",
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowRunsByWorkflow(
    workflowId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/runs?workflow_id=${workflowId}&offset=${offset}&limit=${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowRuns(
    applicationId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter,
    workflowIds?: number[]
  ) {
    let url = `/workflow/runs?application_id=${applicationId}&offset=${offset}&limit=${limit}`;
    if (workflowIds && workflowIds.length > 0) {
      url += `&workflow_ids=${workflowIds.join(",")}`;
    }
    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowRun(
    sessionId: string,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const encodedSessionId = encodeURIComponent(sessionId);
    return GeneralService.get(
      `/workflow/runs/${encodedSessionId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static checkWorkflowTransforms(
    workflowId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/${workflowId}/has-transforms`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static transformPreview(
    workflowId: number,
    sessionId: string,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/workflow/${workflowId}/transform-preview`,
      JSON.stringify({ session_id: sessionId }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestSet(
    testSetId: number,
    limit: number,
    offset: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-sets/${testSetId}?limit=${limit}&offset=${offset}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateWorkflowTestSet(
    testSetId: number,
    name: string,
    description: string | undefined,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = { test_set_name: name };
    if (description !== undefined) {
      body.test_set_description = description;
    }
    return GeneralService.put(
      `/workflow/test-sets/${testSetId}`,
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static addWorkflowTestSetItems(
    testSetId: number,
    workflowRunIds: number[] | null,
    sessionIds: string[] | null,
    workflowId: number | null,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = {};
    if (workflowRunIds && workflowRunIds.length > 0) {
      body.workflow_run_ids = workflowRunIds;
    }
    if (sessionIds && sessionIds.length > 0) {
      body.session_ids = sessionIds;
    }
    if (workflowId) {
      body.workflow_id = workflowId;
    }
    return GeneralService.post(
      `/workflow/test-sets/${testSetId}/items`,
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getImportedSessionIds(
    testSetId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-sets/${testSetId}/imported-session-ids`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflowTestSetItem(
    testSetId: number,
    itemId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/test-sets/${testSetId}/items/${itemId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createWorkflowTestRun(
    testSetId: number,
    workflowId: number | undefined,
    description: string | undefined,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    const body: Record<string, unknown> = { test_set_id: testSetId };
    if (workflowId) body.workflow_id = workflowId;
    if (description) body.description = description;
    return GeneralService.post(
      "/workflow/test-runs",
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRuns(
    testSetId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-runs/by-test-set/${testSetId}?offset=${offset}&limit=${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRun(
    testRunId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-runs/${testRunId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRunsComparison(
    testRunIds: number[],
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/workflow/test-runs/compare",
      JSON.stringify({ test_run_ids: testRunIds, offset, limit }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRunProgress(
    testRunId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/workflow/test-runs/${testRunId}/progress`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getWorkflowTestRunItems(
    testRunId: number,
    offset: number,
    limit: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter,
    matchedFilter?: boolean | null
  ) {
    let url = `/workflow/test-runs/${testRunId}/items?offset=${offset}&limit=${limit}`;
    if (matchedFilter === true) url += "&matched=true";
    else if (matchedFilter === false) url += "&matched=false";
    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflowTestRun(
    testRunId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/test-runs/${testRunId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteWorkflowTestSet(
    testSetId: number,
    loadingHandler: (val: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/workflow/test-sets/${testSetId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
